import { createI18n } from 'vue-i18n';

// One JSON per feature area under src/locales/<locale>/, merged into one message
// tree per locale. `en` is the source of truth in git; every other locale is
// pulled from SimpleLocalize at build time (yarn translations:pull) and ignored by git.
const files = import.meta.glob('./locales/*/*.json', { eager: true, import: 'default' });

// SimpleLocalize may export an untranslated key as "", and vue-i18n treats "" as a
// hit, so drop those leaves to keep the fallback to en working.
export function withoutEmpty(obj) {
  return Object.fromEntries(Object.entries(obj).flatMap(([k, v]) => {
    if (typeof v === 'object' && v !== null) return [[k, withoutEmpty(v)]];
    return v === '' ? [] : [[k, v]];
  }));
}

const messages = {};
Object.entries(files).forEach(([path, json]) => {
  const locale = path.split('/')[2];
  messages[locale] = { ...(messages[locale] || {}), ...withoutEmpty(json) };
});

const availableLocales = Object.keys(messages);

// The user's `language` setting (Settings > Server language, also the Steam bot
// language) is the existing language switcher; these are its codes.
export const USER_LANGUAGE_TO_LOCALE = {
  en: 'en', pt_t: 'pt', dk: 'da', cn: 'zh', de: 'de', pl: 'pl',
};

// Web-only: the game servers have no phrases for these (and no en fallback), so they
// are not in the language setting; users who never picked a language get them from the browser.
const WEB_ONLY_LOCALES = ['fr', 'es'];

// Before sign-in, follow the browser; after sign-in, follow the user setting.
function browserLocale(locales = availableLocales) {
  const lang = typeof navigator !== 'undefined' ? navigator.language : '';
  return [lang, lang?.split('-')[0]].find((c) => c && locales.includes(c)) || 'en';
}

const i18n = createI18n({
  legacy: false,
  locale: browserLocale(),
  fallbackLocale: 'en',
  messages,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
  // Polish has three forms: "1 plik | 2-4 pliki | 5+ plików" (few = 2-4 except 12-14).
  // Clamped so a message with fewer branches still renders its last one.
  pluralRules: {
    pl: (n, choices) => {
      const ten = n % 10; const hundred = n % 100;
      const few = ten >= 2 && ten <= 4 && (hundred < 12 || hundred > 14);
      let index = 2;
      if (n === 1) index = 0;
      else if (few) index = 1;
      return Math.min(index, choices - 1);
    },
  },
});

export function setLocaleForUser(user) {
  const browser = browserLocale(i18n.global.availableLocales);
  // No language = never chose one; an explicit 'en' must stay English.
  const webOnly = !user?.language && WEB_ONLY_LOCALES.includes(browser);
  const wanted = webOnly ? browser : (USER_LANGUAGE_TO_LOCALE[user?.language || 'en'] || browser);
  const next = i18n.global.availableLocales.includes(wanted) ? wanted : 'en';
  i18n.global.locale.value = next;
  document.documentElement.lang = next;
}

// For plain JS modules (store, utils, router) that have no component instance.
export const t = (...args) => i18n.global.t(...args);

export default i18n;
